import { Options, default as Player } from '@vimeo/player';
import React from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { logger } from '$/utils/logger';
import { isEqualDeep, useHasChangedShallow } from '$/hooks/useHasChanged';
import { staticWebinarDimensions, WebinarDimensions } from './context';
import { useDebouncedCallback } from 'use-debounce';
import { VideoPlayerDimensionsOptions } from './types';
import config from '$/config';
import deepCopy from '$/utils/deepCopy';
import { Transitions, defaultTransitions } from './transitions';
import { calcWebinarPlayerDimensions } from './calcWebinarPlayerDimensions';

export type VimeoPlayerOptions = Options;

const collapsingEmbeddedPlayerTransitions = {
  ...defaultTransitions,
  ['video.resize']: { duration: 0.15, type: 'tween' },
};

const videoOptions: Options = {
  quality: undefined, // Add ability to set quality per ewebinar
  speed: true,
  responsive: true,
  title: false,
  portrait: false,
  byline: false,
  loop: false,
  controls: false,
  muted: true,
  autopause: false, // We want to control when we play - unclear if this helps or not
  autoplay: true, // Must be on so that the video has played a bit and seeking shows new frame in player
};

interface WebinarAreaDimensions {
  measured: boolean;
  width: number;
  height: number;
}

interface VideoDimensions {
  measured: boolean;
  width: number;
  height: number;
  aspectRatio: number;
}

interface VideoPlayerDimensions {
  playerIframeRef: (node: HTMLIFrameElement) => void;
  webinarAreaRef: (node: HTMLDivElement) => void;
  dimensions: WebinarDimensions;
  transitions: Transitions;
  player: Player | undefined;
  blocked: boolean;
}

function useVideoPlayerDimensions(
  video: null | {
    vimeoVideoId: number | null;
    vimeoPlayUrl?: string | null;
  },
  options: VideoPlayerDimensionsOptions
): VideoPlayerDimensions {
  const hasPinnedInteraction = options.hasPinnedInteraction && !options.isCollapsedEmbeddedPlayer;
  const [iframeElement, setIFrameElement] = React.useState<HTMLIFrameElement>();
  const playerIframeRef = React.useCallback((node: HTMLIFrameElement) => {
    if (!node) return;
    setIFrameElement(node);
  }, []);

  const [player, setPlayer] = React.useState<Player | undefined>(undefined);

  const [videoDimensions, setVideoDimensions] = React.useState<VideoDimensions>({
    measured: false,
    width: 1920,
    height: 1080,
    aspectRatio: 1920 / 1080,
  });

  const [webinarAreaDimensionsCache, setWebinarAreaDimensionsCache] =
    React.useState<WebinarAreaDimensions>(() => ({
      measured: false,
      ...deepCopy(staticWebinarDimensions.webinarArea),
    }));

  const [ready, setReady] = React.useState(false);
  const [blocked, setBlocked] = React.useState(false);
  const [videoFrameLoaded, setVideoFrameLoaded] = React.useState(false);

  const lastVideoIdRef = React.useRef<number | null>(null);
  const playerStartingRef = React.useRef(false);

  const vimeoVideoId = video?.vimeoVideoId ?? null;
  const videoPlayUrl = video?.vimeoPlayUrl ?? null;

  const updateVideo = React.useCallback(
    (forceLoad?: boolean) => {
      if (!iframeElement) {
        logger.debug('PLAY', 'VIM68 IFRAME NOT READY ', {
          iframe: iframeElement,
          vimeoVideoId: vimeoVideoId,
        });
        return;
      }

      if (player && !forceLoad) {
        return;
      }

      if (playerStartingRef.current) {
        // Already started creating the player
        // Don't do it again
        return;
      }

      logger.debug('PLAY', '109 LOADING NEW VIDEO');

      playerStartingRef.current = true;
      lastVideoIdRef.current = vimeoVideoId;

      logger.debug('PLAY', 'VIM78 NEW PLAYER');
      let newPlayer: Player | undefined = undefined;
      newPlayer = new Player(iframeElement, {
        id: vimeoVideoId!,
        url: videoPlayUrl || undefined,
        ...videoOptions,
      });

      logger.debug('PLAY', 'VIM119 WAIT FOR READY');
      newPlayer
        .ready()
        .then(() => {
          logger.debug('PLAY', 'VIM123 READY - SET VOL 0, PAUSE, PLAYER');
          newPlayer!.setVolume(0);
          newPlayer!.pause();

          setReady(true);
          setBlocked(false);

          if (config.noNetwork) {
            setPlayer(newPlayer);
            setVideoFrameLoaded(true);
          }

          newPlayer!.on('loaded', () => {
            logger.debug('PLAY', 'VIM131 PLAYER LOADED');
            setPlayer(newPlayer);
          });
        })
        .catch((error) => {
          logger.error(`VIM136 ERROR CREATING PLAYER: ${error.message}`, { error });
          setBlocked(true);
        })
        .finally(() => {
          playerStartingRef.current = false;
        });
    },
    [iframeElement, player, videoPlayUrl, vimeoVideoId]
  );

  const hasIFrameChanged = useHasChangedShallow([iframeElement?.id]);
  React.useEffect(() => {
    if (!hasIFrameChanged) return;
    updateVideo(true);
  }, [hasIFrameChanged, updateVideo]);

  const hasVideoIdChanged = useHasChangedShallow([vimeoVideoId, videoPlayUrl]);
  React.useEffect(() => {
    if (!hasVideoIdChanged) return;

    if (!vimeoVideoId) {
      logger.debug('PLAY', 'VIM155 NO VIDEO ID');
      return;
    }

    logger.debug('PLAY', 'VIM159 New VIDEO ID');
    updateVideo();
  }, [hasVideoIdChanged, player, updateVideo, videoPlayUrl, vimeoVideoId]);

  React.useEffect(() => {
    if (!hasVideoIdChanged || !player) return;

    // Handle video change when we have a player already
    if (lastVideoIdRef.current === vimeoVideoId) {
      return;
    }
    // update vimeo id
    lastVideoIdRef.current = vimeoVideoId;

    // if vimeo id changes, reload player
    if (vimeoVideoId) {
      player.loadVideo({
        id: vimeoVideoId!,
        url: videoPlayUrl || undefined,
        ...videoOptions,
      });
    } else {
      player.unload();
    }
  }, [hasVideoIdChanged, player, videoPlayUrl, vimeoVideoId]);

  const onWebinarAreaResizeNow = React.useCallback(
    async ({ height, width }: { height: number; width: number }) => {
      setWebinarAreaDimensionsCache({ measured: true, width, height });

      logger.debug('RESIZE', '144 webinar area resize', { width, height });
      let aspectRatio = 0;

      // set aspect ratio
      if (player && !videoDimensions.measured) {
        // use player when available
        const [vWidth, vHeight] = await Promise.all([
          player.getVideoWidth(),
          player.getVideoHeight(),
        ]);

        aspectRatio = vWidth / vHeight;
        logger.debug('RESIZE', '75 player w', vWidth, '/h', vHeight, ' =', aspectRatio);

        // set aspect ratio
        setVideoDimensions({ measured: true, width: vWidth, height: vHeight, aspectRatio });
      }
    },
    [player, videoDimensions.measured]
  );

  const onWebinarAreaResize = useDebouncedCallback(onWebinarAreaResizeNow, 80, {
    leading: false,
    trailing: true,
  });

  const [webinarAreaDiv, setWebinarAreaDiv] = React.useState<HTMLDivElement>();
  const webinarAreaRefCallback = React.useCallback(
    (node: HTMLDivElement) => {
      if (!node) return;

      setWebinarAreaDiv(node);
      onWebinarAreaResize({
        height: node.clientHeight,
        width: node.clientWidth,
      });
    },
    [onWebinarAreaResize]
  );

  React.useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { height, width } = entry.contentRect;
        onWebinarAreaResize({ height: height, width });
      }
    });

    // observe ref changes
    if (webinarAreaDiv?.clientHeight) {
      logger.debug('RESIZE', 'useEffect parent height ', webinarAreaDiv.clientHeight);

      // Call onResize at least once
      onWebinarAreaResize({
        height: webinarAreaDiv.clientHeight,
        width: webinarAreaDiv.clientWidth,
      });

      ro.observe(webinarAreaDiv);
    }

    const current = webinarAreaDiv;
    return (): void => {
      current && ro.unobserve(current);
    };
  }, [vimeoVideoId, webinarAreaDiv, player, onWebinarAreaResize]);

  /**
   * Iframe does not support "onerror" event but "onload" always happen
   * whether iframe is blocked or not
   */
  React.useEffect(() => {
    if (!iframeElement) return;
    const onLoad = (): void => setVideoFrameLoaded(true);

    iframeElement.addEventListener('load', onLoad);
    const current = iframeElement;
    return (): void => {
      current.removeEventListener('load', onLoad);
    };
  }, [iframeElement]);

  /**
   * Since we cannot use "onload" event to determine a blocked video.
   * We use "player.ready()" to determine instead. But "player.ready()" is a bit
   * slower than "onload" event of iframe if iframe loaded succesfully.
   * so that we need to delay some seconds to make sure "ready()" get executed.
   */
  React.useEffect(() => {
    if (!videoFrameLoaded || ready) return;

    const timer = setTimeout(() => {
      if (!ready) setBlocked(true);
    }, 10000);
    return (): void => {
      timer && clearTimeout(timer);
    };
  }, [videoFrameLoaded, ready]);

  const calcResult = React.useCallback(() => {
    const windowArea = { height: window.innerHeight, width: window.innerWidth };
    const useVimeoResult: VideoPlayerDimensions = {
      playerIframeRef,
      webinarAreaRef: webinarAreaRefCallback,
      blocked,
      player,
      transitions: isCollapsingEmbeddedPlayerTransition.current
        ? collapsingEmbeddedPlayerTransitions
        : defaultTransitions,
      dimensions: calcWebinarPlayerDimensions({
        options,
        webinarAreaDimensionsCache,
        videoDimensions,
        blocked,
        hasPinnedInteraction,
        vimeoVideoId,
        webinarAreaDiv,
        windowArea,
      }),
    };

    return useVimeoResult;
  }, [
    options,
    webinarAreaDimensionsCache,
    hasPinnedInteraction,
    webinarAreaDiv,
    videoDimensions,
    playerIframeRef,
    webinarAreaRefCallback,
    blocked,
    player,
    vimeoVideoId,
  ]);

  const isCollapsingEmbeddedPlayerTransition = React.useRef(false);
  const [vimeoResult, setVimeoResult] = React.useState<VideoPlayerDimensions>(() => calcResult());

  const reCalcResult = React.useCallback(() => {
    const useVimeoResult = calcResult();

    logger.debug('ANIM', 'Webinar Dimensions ', {
      actionsPaneState: options.actionsPaneState,
      ...useVimeoResult.dimensions,
    });

    const keys: (keyof VideoPlayerDimensions)[] = [
      'blocked',
      'player',
      'playerIframeRef',
      'webinarAreaRef',
    ];
    if (
      keys.some((key) => vimeoResult[key] !== useVimeoResult[key]) ||
      !isEqualDeep(vimeoResult.dimensions, useVimeoResult.dimensions)
    ) {
      setVimeoResult(useVimeoResult);
    }

    // Also on hasChat change
  }, [vimeoResult, calcResult, setVimeoResult, options.actionsPaneState]);

  const debouncedReCalcResult = useDebouncedCallback(reCalcResult, 300, {
    leading: false,
    trailing: true,
  });

  const isCollapsedEmbeddedPlayerChanged = useHasChangedShallow([
    options.isCollapsedEmbeddedPlayer,
  ]);
  const transitionTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  React.useEffect(() => {
    if (!options.isEmbeddedPlayer || !isCollapsedEmbeddedPlayerChanged) return;

    // make transition faster when grow/shrink the embedded player
    isCollapsingEmbeddedPlayerTransition.current = true;
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = setTimeout(() => {
      isCollapsingEmbeddedPlayerTransition.current = false;
      reCalcResult();
    }, 500);
    reCalcResult();
  }, [isCollapsedEmbeddedPlayerChanged, options.isEmbeddedPlayer, reCalcResult]);

  React.useEffect(() => {
    debouncedReCalcResult();
  }, [
    vimeoResult,
    calcResult,
    setVimeoResult,
    options.hasChat,
    options.actionsPaneState,
    options.hasPinnedInteraction,
    options.isCollapsedEmbeddedPlayer,
    options.embeddedPlayerInfo,
    debouncedReCalcResult,
  ]);

  return vimeoResult;
}

export default useVideoPlayerDimensions;
